import {
  computed,
  action,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { v4 as uuid } from 'uuid';

export enum NotificationType {
  SUCCESS = 'Success',
  ERROR = 'Danger',
  INFO = 'Info',
}

export interface INotification {
  message: string;
  type: NotificationType;
  sticky: boolean;
  body: React.ReactNode | undefined;
}

export class NotificationStore {
  @observable notificationsMap = observable.map<string, INotification>([]);

  constructor() {
    makeObservable(this, undefined, {
      autoBind: true,
    }); //use decorators
  }

  @computed get notifications() {
    return Array.from(this.notificationsMap.entries());
  }

  @action.bound success(
    message: string,
    sticky: boolean = false,
    body: React.ReactNode | undefined = undefined,
  ) {
    this.notificationsMap.set(uuid(), {
      message,
      type: NotificationType.SUCCESS,
      sticky,
      body,
    });
  }

  @action.bound onSuccess<T>(
    message: string,
    sticky: boolean = false,
    body: React.ReactNode | undefined = undefined,
  ) {
    return (result: T) => {
      runInAction(() => {
        this.notificationsMap.set(uuid(), {
          message,
          type: NotificationType.SUCCESS,
          sticky,
          body,
        });
      });

      return result;
    };
  }

  @action.bound error(
    message: string,
    sticky: boolean = false,
    body: React.ReactNode | undefined = undefined,
  ) {
    this.notificationsMap.set(uuid(), {
      message,
      type: NotificationType.ERROR,
      sticky,
      body,
    });
  }

  @action.bound onError<T>(
    message: string,
    def: T,
    sticky: boolean = false,
    body: string | undefined = undefined,
  ) {
    return (result: T) => {
      runInAction(() => {
        this.notificationsMap.set(uuid(), {
          message: `${message} \n ${result}`,
          type: NotificationType.ERROR,
          sticky,
          body,
        });
      });

      return def;
    };
  }

  @action.bound info(
    message: string,
    sticky: boolean = false,
    body: React.ReactNode | undefined = undefined,
  ) {
    this.notificationsMap.set(uuid(), {
      message,
      type: NotificationType.INFO,
      sticky,
      body,
    });
  }

  @action.bound onInfo<T>(
    message: string,
    def: T,
    sticky: boolean = false,
    body: React.ReactNode | undefined = undefined,
  ) {
    return (result: T) => {
      runInAction(() => {
        this.notificationsMap.set(uuid(), {
          message: `${message} \n ${result}`,
          type: NotificationType.INFO,
          sticky,
          body,
        });
      });

      return def;
    };
  }

  @action.bound setNotification(key: string, notification: INotification) {
    this.notificationsMap.set(key, notification);
  }

  @action.bound clearNotification(uuid: string) {
    this.notificationsMap.delete(uuid);
  }
}
export const NotificationStoreInstance = new NotificationStore();
